/** https://ant.design/components/alert/#API */
import React from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { Alert as AntdAlert } from 'antd'
import {
  alertIconColorByType,
  bannerDecorationIconByType,
  alertIconByType,
  alertWithDecoration,
  alertMessagePrefixByType,
} from 'components/PageMeta/utils/alert-style-helper'
import { compose } from 'ramda'
import Button from 'components/patterns/Button'
import withStyle, {
  LinkBox,
  AlertGridIcon,
  StyledIcon,
  StyledAlertIconContainer,
  MessagePrefix,
  MessageContainer,
} from './Alert.style'

const Alert = ({
  persistent,
  type = '',
  message,
  className,
  closable,
  onClose,
  closeText,
  actionTrigger,
  reportLink,
  showIcon,
}) => {
  const dispatch = useDispatch()
  const hasDecoration = alertWithDecoration.includes(type)
  const iconToDisplay = (
    <StyledAlertIconContainer>
      {hasDecoration && (
        <AlertGridIcon
          icon={bannerDecorationIconByType[type]}
          fill={alertIconColorByType[type]}
        />
      )}
      <StyledIcon
        hasDecoration={hasDecoration}
        icon={alertIconByType[type]}
        fill={alertIconColorByType[type]}
      />
    </StyledAlertIconContainer>
  )
  const renderActionTrigger = () => {
    const {
      text,
      onClick,
      href,
      needsDispatch,
      onColor = 'light',
      type = 'primary',
      disabled,
      dataTestId,
    } = actionTrigger

    if (href) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginLeft: 8, fontSize: 12, color: 'inherit' }}
        >
          {text}
        </a>
      )
    }

    if (onClick) {
      return (
        <Button
          onColor={onColor}
          size="small"
          onClick={(e) => (needsDispatch ? dispatch(onClick(e)) : onClick(e))}
          type={type}
          disabled={disabled}
          dataTestId={dataTestId}
        >
          {text}
        </Button>
      )
    }
    return <LinkBox>{actionTrigger}</LinkBox>
  }

  const description =
    actionTrigger || reportLink ? (
      <>
        <MessageContainer>
          <MessagePrefix type={type}>
            {alertMessagePrefixByType[type]}
          </MessagePrefix>
          {message}
        </MessageContainer>{' '}
        {actionTrigger && renderActionTrigger()}
        {reportLink && (
          <>
            <a
              href={reportLink.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginLeft: 4, color: 'inherit' }}
            >
              {reportLink.text}
            </a>
            {reportLink.trailingPeriod && '.'}
          </>
        )}
      </>
    ) : (
      <MessageContainer>
        <MessagePrefix type={type}>
          {alertMessagePrefixByType[type]}
        </MessagePrefix>
        {message}
      </MessageContainer>
    )

  return (
    <AntdAlert
      type={type}
      description={description}
      className={className}
      closable={closable || !persistent}
      showIcon={showIcon}
      onClose={onClose}
      icon={iconToDisplay}
      closeText={closeText}
      banner
    />
  )
}

Alert.defaultProps = {
  showIcon: true,
  icon: false,
  closeText: false,
  reportLink: null,
}

Alert.propTypes = {
  /** Alert Types */
  type: PropTypes.oneOf(['success', 'info', 'warning', 'error']),
  /** Alert message to user - Message is used in the Ant Design description field to produce the outline style icon.  Used in the message prop will produce a filled in Icon */
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Additional classname */
  className: PropTypes.string,
  /** Show closable icon and allow user to close */
  closable: PropTypes.bool,
  /** OnClose click handler */
  onClose: PropTypes.func,
  /** This is a persistant type alert - this is tied to redux and the pattern of how Alerts/Toast are used.  Should be refactored to remove this. */
  persistent: PropTypes.bool,
  /** Show the alert Icon */
  showIcon: PropTypes.bool,
  /** Custom Icon  */
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.bool, PropTypes.string]),
  /** Text or node - used in place of the default X close icon */
  closeText: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  /** Action trigger - object will create a button and node will be wrapped into one. the button will be appended to the message */
  actionTrigger: PropTypes.oneOfType([
    PropTypes.shape({
      // the text in the button
      text: PropTypes.string,
      // action that is triggered on click of the button
      onClick: PropTypes.func,
      // color the secondary button for different background
      onColor: PropTypes.oneOf(['light', 'dark']),
      // disables the action button (e.g. while an async operation is in progress)
      disabled: PropTypes.bool,
    }),
    PropTypes.node,
  ]),
  reportLink: PropTypes.shape({
    href: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    trailingPeriod: PropTypes.bool,
  }),
}

export default compose(withStyle)(Alert)
